import { useColorModeValue } from "@/components/ui/color-mode"
import { effects } from "@/theme"
import { Box, type BoxProps } from "@chakra-ui/react"
import { type ReactNode, forwardRef } from "react"

export interface IconBoxProps extends BoxProps {
  children: ReactNode
  variant?: "solid" | "outline" | "gradient" | "glass"
  size?: "sm" | "md" | "lg" | "xl"
}

const sizeMap = {
  sm: { w: "8", h: "8", fontSize: "md" },
  md: { w: "12", h: "12", fontSize: "lg" },
  lg: { w: "16", h: "16", fontSize: "xl" },
  xl: { w: "20", h: "20", fontSize: "2xl" },
}

export const IconBox = forwardRef<HTMLDivElement, IconBoxProps>(
  ({ children, variant = "glass", size = "md", ...props }, ref) => {
    const sizeProps = sizeMap[size]
    const isLight = useColorModeValue(true, false)

    const getVariantStyles = () => {
      switch (variant) {
        case "solid":
          return {
            bg: "brand.default",
            color: "white",
          }
        case "outline":
          return {
            bg: "transparent",
            border: "1px solid",
            borderColor: isLight ? "gray.200" : "border.default",
            color: isLight ? "gray.600" : "fg.default",
          }
        case "gradient":
          return {
            bgGradient: effects.gradients.brandGradient,
            color: "white",
          }
        default:
          return {
            bg: isLight ? "gray.100" : "bg.glass",
            backdropFilter: isLight ? "none" : "blur(20px)",
            border: "1px solid",
            borderColor: isLight ? "gray.200" : "border.default",
            color: isLight ? "gray.600" : "fg.default",
          }
      }
    }

    return (
      <Box
        ref={ref}
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="12px"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{
          transform: "translateY(-2px)",
          boxShadow: effects.shadows.button,
        }}
        {...sizeProps}
        {...getVariantStyles()}
        {...props}
      >
        {children}
      </Box>
    )
  },
)

IconBox.displayName = "IconBox"
